import { SignalRTransport } from "./transports/SignalRTransport";
import { ServerEvents, ClientMethods, ConnectionState } from "./types/events";

/**
 * RealtimeService (Central Hub & Gateway)
 * Manages active transport, event routing, typed actions, and reconnection.
 * Designed with modern singleton & adapter patterns.
 */
class RealtimeService {
    constructor() {
        this.transport = new SignalRTransport();
        this.currentUser = null;
        this.isInitialized = false;
    }

    /**
     * Swap transport at runtime (e.g. to WebSocketTransport / uWebSockets)
     * @param {IRealtimeTransport} newTransport
     */
    async setTransport(newTransport) {
        if (this.transport) {
            await this.transport.disconnect();
        }
        this.transport = newTransport;
        if (this.isInitialized) {
            await this.init();
        }
    }

    /**
     * Initialize the realtime connection
     * @param {Object} config
     */
    async init(config = {}) {
        if (this.isInitialized && this.transport.getState() === ConnectionState.CONNECTED) {
            return;
        }
        this.isInitialized = true;
        try {
            await this.transport.connect(config);
            if (this.currentUser) {
                await this.registerUser(this.currentUser);
            }
        } catch (err) {
            console.error("[RealtimeService] Initialization error:", err);
        }
    }

    /**
     * Disconnect connection
     */
    async disconnect() {
        if (this.currentUser) {
            await this.userLogout(this.currentUser);
        }
        await this.transport.disconnect();
        this.isInitialized = false;
    }

    // ==========================================
    // Event Subscriptions (Listeners)
    // ==========================================

    on(eventName, handler) {
        this.transport.on(eventName, handler);
        return () => this.transport.off(eventName, handler);
    }

    off(eventName, handler) {
        this.transport.off(eventName, handler);
    }

    onOnlineUsers(handler) {
        return this.on(ServerEvents.ONLINE_USERS, handler);
    }

    onNotification(handler) {
        return this.on(ServerEvents.SEND_NOTIFICATION, handler);
    }

    onUpdatePage(handler) {
        return this.on(ServerEvents.SEND_UPDATE_PAGE, handler);
    }

    onConnectionStateChange(handler) {
        if (typeof this.transport.onStateChange === "function") {
            return this.transport.onStateChange(handler);
        }
        return () => {};
    }

    getConnectionId() {
        return this.transport.getConnectionId();
    }

    getConnectionState() {
        return this.transport.getState();
    }

    // ==========================================
    // Outgoing Client Methods (Actions)
    // ==========================================

    async registerUser(user) {
        this.currentUser = user;
        if (!user) return;
        return await this.transport.invoke(ClientMethods.REGISTER_USER, {
            Id: user.Id,
            Username: user.Username,
            Name: user.Name,
            Email: user.Email,
            Phone: user.Phone,
            Sex: user.Sex,
            Country: user.Country,
            Birthday: user.Birthday,
            ActivateUntilDate: user.ActivateUntilDate,
            UserTypeId: user.UserType ? user.UserType.Id : (user.UserTypeId || null),
            UserType: user.UserType,
            SchoolId: user.School ? user.School.Id : (user.SchoolId || null),
            School: user.School
        });
    }

    async getOnlineUsers() {
        return await this.transport.invoke(ClientMethods.GET_ONLINE_USERS);
    }

    async userLogout(user) {
        const userId = user?.Id || user;
        if (userId) {
            await this.transport.invoke(ClientMethods.USER_LOGOUT, userId);
        }
        this.currentUser = null;
    }

    async setPageTitle(userId, title, location) {
        if (!userId) return;
        return await this.transport.invoke(ClientMethods.SET_PAGE_TITLE, {
            UserId: userId,
            Title: title,
            Location: location
        });
    }

    async sendEmail(title, message, users) {
        return await this.transport.invoke(ClientMethods.SEND_EMAIL, {
            Title: title,
            Message: message,
            Users: users
        });
    }

    async shareFile(studentIds, file) {
        const ids = Array.isArray(studentIds) ? studentIds : [studentIds];
        return await this.transport.invoke(ClientMethods.SHARE_FILE, {
            StudentId: ids,
            Data: file
        });
    }

    async shareLesson(studentIds, lesson, indexType) {
        const ids = Array.isArray(studentIds) ? studentIds : [studentIds];
        return await this.transport.invoke(ClientMethods.SHARE_LESSON, {
            StudentId: ids,
            Data: lesson,
            IndexType: indexType
        });
    }

    async shareMission(studentIds, mission) {
        const ids = Array.isArray(studentIds) ? studentIds : [studentIds];
        return await this.transport.invoke(ClientMethods.SHARE_MISSION, {
            StudentId: ids,
            Data: mission
        });
    }

    async notificationIsView(notificationType, userId) {
        return await this.transport.invoke(ClientMethods.NOTIFICATION_IS_VIEW, notificationType, userId);
    }

    async sendContactUs(title) {
        return await this.transport.invoke(ClientMethods.SEND_CONTACT_US, title);
    }

    async schoolRegister(title) {
        return await this.transport.invoke(ClientMethods.SCHOOL_REGISTER, title);
    }

    async errorReport() {
        return await this.transport.invoke(ClientMethods.ERROR_REPORT);
    }

    async errorReportIsSolve(errorReport, targetUserId) {
        const userId = targetUserId || (errorReport?.User?.Id);
        return await this.transport.invoke(ClientMethods.ERROR_REPORT_IS_SOLVE, errorReport, userId);
    }

    async updateGameScore(userId) {
        return await this.transport.invoke(ClientMethods.UPDATE_GAME_SCORE, userId);
    }
}

// Export singleton instance
export const realtimeService = new RealtimeService();
export default realtimeService;
